import React, { FC, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import dayjs from 'dayjs';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Autocomplete,
	TextField,
	Box,
	Typography,
	CircularProgress,
} from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
	getFullscreenContainer,
	getFullscreenPopperSlotProps,
} from '../../utils/fullscreenContainer';

export type ShiftSchedulerDialogMode =
	| 'quick-edit-day'
	| 'quick-edit-week'
	| 'create-day'
	| 'create-week';

export interface ShiftCreateContext {
	startAt: Date;
	endAt: Date;
	jobCodeId?: string | number;
	employeeId?: string | number | null;
}

interface ShiftSchedulerDialogProps {
	open: boolean;
	mode: ShiftSchedulerDialogMode;
	shift?: any;
	createContext?: ShiftCreateContext;
	employees: any[];
	jobCodes: any[];
	isSaving?: boolean;
	onClose: () => void;
	onSaveDayUser: (employeeId: string | null) => void;
	onSaveWeekFields: (fields: { jobCode: string; startAt: string; endAt: string }) => void;
	onCreateDay: (employeeId: string | null) => void;
	onCreateWeek: (fields: { jobCode: string; startAt: string; endAt: string }) => void;
	onOpenFullEdit?: () => void;
}

const HOUSE_SHIFT_OPTION = { id: 'HOUSE_SHIFT', firstName: 'House', lastName: 'Shift', '@id': 'null' };

const ShiftSchedulerDialog: FC<ShiftSchedulerDialogProps> = ({
	open,
	mode,
	shift,
	createContext,
	employees,
	jobCodes,
	isSaving = false,
	onClose,
	onSaveDayUser,
	onSaveWeekFields,
	onCreateDay,
	onCreateWeek,
	onOpenFullEdit,
}) => {
	const isDayMode = mode === 'quick-edit-day' || mode === 'create-day';
	const isCreateMode = mode === 'create-day' || mode === 'create-week';
	const isQuickEdit = mode === 'quick-edit-day' || mode === 'quick-edit-week';

	const sortedEmployees = useMemo(() => {
		const filtered = (employees || []).filter((emp) => {
			const email = emp.email?.toLowerCase() || '';
			return !email.endsWith('@byod.ai');
		});
		return [...filtered].sort((a, b) =>
			`${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
		);
	}, [employees]);

	const employeeOptions = useMemo(
		() => [HOUSE_SHIFT_OPTION, ...sortedEmployees],
		[sortedEmployees]
	);

	const sortedJobCodes = useMemo(() => {
		if (!jobCodes) return [];
		return [...jobCodes].sort((a, b) => (a.jobNumber ?? 999) - (b.jobNumber ?? 999));
	}, [jobCodes]);

	const getInitialEmployee = () => {
		if (isCreateMode) return HOUSE_SHIFT_OPTION;
		if (!shift?.employee) return HOUSE_SHIFT_OPTION;
		return (
			sortedEmployees.find(
				(e) => e.id === shift.employee.id || e['@id'] === shift.employee['@id']
			) || HOUSE_SHIFT_OPTION
		);
	};

	const getInitialJobCode = () => {
		if (createContext?.jobCodeId) {
			return (
				sortedJobCodes.find(
					(jc) => jc.id === createContext.jobCodeId || String(jc.id) === String(createContext.jobCodeId)
				) || null
			);
		}
		if (shift?.jobCode) {
			return (
				sortedJobCodes.find(
					(jc) => jc.id === shift.jobCode.id || jc['@id'] === shift.jobCode['@id']
				) || null
			);
		}
		return null;
	};

	const getInitialStartAt = () => {
		if (createContext?.startAt) return moment(createContext.startAt).toISOString();
		if (shift?.startAt || shift?.start) {
			return moment(shift.startAt || shift.start).toISOString();
		}
		return moment().toISOString();
	};

	const getInitialEndAt = () => {
		if (createContext?.endAt) return moment(createContext.endAt).toISOString();
		if (shift?.endAt || shift?.end) {
			return moment(shift.endAt || shift.end).toISOString();
		}
		return moment().add(8, 'hours').toISOString();
	};

	const [selectedEmployee, setSelectedEmployee] = useState<any>(getInitialEmployee());
	const [selectedJobCode, setSelectedJobCode] = useState<any>(getInitialJobCode());
	const [startAt, setStartAt] = useState<string>(getInitialStartAt());
	const [endAt, setEndAt] = useState<string>(getInitialEndAt());
	const [portalContainer, setPortalContainer] = useState<HTMLElement | undefined>(undefined);

	useEffect(() => {
		if (!open) return;

		const syncPortalContainer = () => {
			setPortalContainer(getFullscreenContainer());
		};

		syncPortalContainer();
		document.addEventListener('fullscreenchange', syncPortalContainer);
		return () => document.removeEventListener('fullscreenchange', syncPortalContainer);
	}, [open]);

	useEffect(() => {
		if (!open) return;
		setSelectedEmployee(getInitialEmployee());
		setSelectedJobCode(getInitialJobCode());
		setStartAt(getInitialStartAt());
		setEndAt(getInitialEndAt());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, mode, shift, createContext]);

	const dialogTitle = useMemo(() => {
		if (isCreateMode) return 'Create Shift';
		return 'Quick Edit';
	}, [isCreateMode]);

	const handleEmployeeChange = (_: unknown, value: any) => {
		setSelectedEmployee(value || HOUSE_SHIFT_OPTION);
		const employeeId = value?.['@id'] === 'null' ? null : value?.['@id'] || null;

		if (mode === 'quick-edit-day') {
			onSaveDayUser(employeeId);
		}
	};

	const handleCreate = () => {
		if (mode === 'create-day') {
			const employeeId =
				selectedEmployee?.['@id'] === 'null' ? null : selectedEmployee?.['@id'] || null;
			onCreateDay(employeeId);
			return;
		}

		if (mode === 'create-week' && selectedJobCode) {
			onCreateWeek({
				jobCode: selectedJobCode['@id'],
				startAt,
				endAt,
			});
			return;
		}

		if (mode === 'quick-edit-week' && selectedJobCode) {
			onSaveWeekFields({
				jobCode: selectedJobCode['@id'],
				startAt,
				endAt,
			});
		}
	};

	const handleTimeChange = (field: 'startAt' | 'endAt', timeValue: dayjs.Dayjs | null) => {
		if (!timeValue) return;
		const currentIso = field === 'startAt' ? startAt : endAt;
		const currentDate = moment(currentIso).format('YYYY-MM-DD');
		const updated = moment(`${currentDate} ${timeValue.format('HH:mm')}:00`, 'YYYY-MM-DD HH:mm:ss');
		const iso = updated.toISOString();
		if (field === 'startAt') setStartAt(iso);
		else setEndAt(iso);
	};

	const canSubmitWeek =
		Boolean(selectedJobCode) && moment(endAt).isAfter(moment(startAt));

	const popperSlotProps = getFullscreenPopperSlotProps(portalContainer);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			container={portalContainer}
			aria-labelledby="shift-scheduler-dialog-title"
		>
			<DialogTitle id="shift-scheduler-dialog-title">{dialogTitle}</DialogTitle>
			<DialogContent>
				{isDayMode && (
					<Box sx={{ mt: 1 }}>
						<Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
							Assigned User
						</Typography>
						<Autocomplete
							options={employeeOptions}
							getOptionLabel={(option) =>
								option.id === 'HOUSE_SHIFT'
									? 'House Shift'
									: `${option.firstName} ${option.lastName}`
							}
							value={selectedEmployee}
							onChange={handleEmployeeChange}
							isOptionEqualToValue={(option, value) => option['@id'] === value?.['@id']}
							disabled={isSaving}
							slotProps={popperSlotProps}
							renderInput={(params) => (
								<TextField {...params} placeholder="Select employee" size="small" />
							)}
						/>
					</Box>
				)}

				{!isDayMode && (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
						<Box>
							<Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
								Job Code
							</Typography>
							<Autocomplete
								options={sortedJobCodes}
								getOptionLabel={(option) =>
									option.jobNumber ? `${option.jobNumber} - ${option.label}` : option.label
								}
								value={selectedJobCode}
								onChange={(_, value) => setSelectedJobCode(value)}
								isOptionEqualToValue={(option, value) => option['@id'] === value?.['@id']}
								disabled={isSaving}
								slotProps={popperSlotProps}
								renderInput={(params) => (
									<TextField {...params} placeholder="Select job code" size="small" required />
								)}
							/>
						</Box>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<Box sx={{ display: 'flex', gap: 2 }}>
								<Box sx={{ flex: 1 }}>
									<Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
										Start Time
									</Typography>
									<TimePicker
										value={dayjs(startAt).isValid() ? dayjs(startAt) : null}
										onChange={(value) => handleTimeChange('startAt', value)}
										disabled={isSaving}
										slotProps={{
											textField: { size: 'small', fullWidth: true },
											...popperSlotProps,
										}}
									/>
								</Box>
								<Box sx={{ flex: 1 }}>
									<Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
										End Time
									</Typography>
									<TimePicker
										value={dayjs(endAt).isValid() ? dayjs(endAt) : null}
										onChange={(value) => handleTimeChange('endAt', value)}
										disabled={isSaving}
										slotProps={{
											textField: { size: 'small', fullWidth: true },
											...popperSlotProps,
										}}
									/>
								</Box>
							</Box>
						</LocalizationProvider>
					</Box>
				)}
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
				<Box>
					{isQuickEdit && onOpenFullEdit && (
						<Button onClick={onOpenFullEdit} disabled={isSaving} sx={{ textTransform: 'none' }}>
							Open Full Edit
						</Button>
					)}
				</Box>
				<Box sx={{ display: 'flex', gap: 1 }}>
					<Button onClick={onClose} disabled={isSaving} sx={{ textTransform: 'none' }}>
						{mode === 'quick-edit-day' ? 'Close' : 'Cancel'}
					</Button>
					{(!isQuickEdit || !isDayMode) && (
						<Button
							variant="contained"
							onClick={handleCreate}
							disabled={isSaving || (isDayMode ? false : !canSubmitWeek)}
							sx={{
								textTransform: 'none',
								backgroundColor: 'rgb(68, 99, 136)',
								'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
							}}
						>
							{isSaving ? <CircularProgress size={18} color="inherit" /> : isCreateMode ? 'Create' : 'Save'}
						</Button>
					)}
				</Box>
			</DialogActions>
		</Dialog>
	);
};

export default ShiftSchedulerDialog;
